import React from "react";

import { mdiLock } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import SwitchOnOff from "../../common/tools/SwitchOnOff";
import InfoTooltip from "../../utils/InfoTooltip";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type {
  PeriodLabel,
  StorefrontProjectParams,
} from "../../../api/ProjectAPI.types";

interface PeriodTopLabelOption {
  value: StorefrontProjectParams["period_top_label"];
  label: string;
}

interface PeriodLabelOption {
  value: PeriodLabel;
  label: string;
}

interface LabelsConfigurationProps {
  hasDisplayFreeLabel: boolean;
  hasDisplayLiveLabel: boolean;
  hasDisplayReplayLabel: boolean;
  hasDisplayTopLabel: boolean;
  hasDisplayComingSoonLabel: boolean;
  hasDisplayNewLabel: boolean;
  hasDisplayLastChanceLabel: boolean;
  hasDisplayTvodLabel: boolean;
  periodTopLabel: StorefrontProjectParams["period_top_label"];
  periodNewLabel: StorefrontProjectParams["period_new_label"];
  periodLastChanceLabel: StorefrontProjectParams["period_last_chance_label"];
  periodComingSoonLabel: StorefrontProjectParams["period_coming_soon_label"];
  onChange: <K extends keyof StorefrontProjectParams>(
    key: K,
    value: StorefrontProjectParams[K],
  ) => void;
}

function LabelsConfiguration({
  hasDisplayFreeLabel,
  hasDisplayLiveLabel,
  hasDisplayReplayLabel,
  hasDisplayTopLabel,
  hasDisplayNewLabel,
  hasDisplayLastChanceLabel,
  hasDisplayTvodLabel,
  hasDisplayComingSoonLabel,
  periodTopLabel,
  periodNewLabel,
  periodLastChanceLabel,
  periodComingSoonLabel,
  onChange,
}: LabelsConfigurationProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const periodTopLabelOptionList: PeriodTopLabelOption[] = [
    { value: "week", label: t("period.week") },
    { value: "month", label: t("period.month") },
    { value: "threeMonths", label: t("period.threeMonths") },
    { value: "year", label: t("period.year") },
    { value: "all", label: t("period.all") },
  ];

  const periodLabelOptionList: PeriodLabelOption[] = [
    { value: "week", label: t("period.oneWeek") },
    { value: "twoWeeks", label: t("period.twoWeeks") },
    { value: "month", label: t("period.oneMonth") },
  ];

  const handleChangeTopLabel = (value: boolean): void => {
    onChange("display_top_label", value);
    if (value && !periodTopLabel) {
      onChange("period_top_label", "week");
    }
  };

  const handleChangeTvodLabel = (value: boolean): void => {
    onChange("display_tvod_label", value);
  };

  const handleChangeNewLabel = (value: boolean): void => {
    onChange("display_new_label", value);
    if (value && !periodNewLabel) {
      onChange("period_new_label", "week");
    }
  };

  const handleChangeLastChanceLabel = (value: boolean): void => {
    onChange("display_last_chance_label", value);
    if (value && !periodLastChanceLabel) {
      onChange("period_last_chance_label", "week");
    }
  };

  const handleChangeComingSoonLabel = (value: boolean): void => {
    onChange("display_coming_soon_label", value);
    if (value && !periodComingSoonLabel) {
      onChange("period_coming_soon_label", "week");
    }
  };

  return (
    <div className="editor-form-section section-separator">
      <div className="editor-form-section-title">{t("labelsSettings")}</div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayFreeLabel ?? false}
            name="display_free_label"
            onChange={(value: boolean): void => {
              onChange("display_free_label", value);
            }}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="editor-label-tag free">{t("free_label")}</div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayLiveLabel ?? false}
            name="display_live_label"
            onChange={(value: boolean): void => {
              onChange("display_live_label", value);
            }}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="editor-label-tag live">{t("live_label")}</div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayReplayLabel ?? false}
            name="display_replay_label"
            onChange={(value: boolean): void => {
              onChange("display_replay_label", value);
            }}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="editor-label-tag replay">{t("replay_label")}</div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayTvodLabel ?? false}
            name="display_tvod_label"
            onChange={handleChangeTvodLabel}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          Tvod
          <div className="text-overlay-foreground ml-[0.3125rem] flex items-center justify-center rounded-[0.234375rem] bg-[#5d5d5d] p-[0.15625rem]">
            <Icon path={mdiLock} size={0.75} />
          </div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayTopLabel ?? false}
            name="display_top_label"
            onChange={handleChangeTopLabel}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div
            className="text-overlay-foreground m-[0.3125rem] h-[2.5rem] bg-[#5d5d5d] p-[0.3125rem]"
            style={{
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 85%)",
            }}
          >
            <div className="text-center font-semibold uppercase">
              <span className="block text-[0.703125rem] leading-none">Top</span>
              <span className="block text-[0.9375rem] leading-none">10</span>
            </div>
          </div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row">
        {t("periodLabel.periodTopTenLabel")}
        <div className="editor-form-input">
          <Select
            className="editor-select max-w-xl"
            classNamePrefix="slct"
            isDisabled={!hasDisplayTopLabel}
            name="periodtopLabel"
            options={periodTopLabelOptionList}
            placeholder={t("period.placeholder")}
            onChange={(
              selectedOption: SingleValue<PeriodTopLabelOption>,
            ): void => {
              onChange(
                "period_top_label",
                selectedOption ? selectedOption.value : "week",
              );
            }}
            value={
              periodTopLabelOptionList.find(
                (option) => option.value === periodTopLabel,
              ) ?? periodTopLabelOptionList[0]
            }
          />
        </div>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayNewLabel ?? false}
            name="display_new_label"
            onChange={handleChangeNewLabel}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="text-overlay-foreground m-[0.3125rem] rounded-[0.234375rem] bg-[#5d5d5d] px-[0.3125rem] text-center font-semibold">
            <span className="block text-[0.78125rem]">{t("newLabel")}</span>
          </div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row">
        {t("periodLabel.periodNewLabel")}
        <div className="editor-form-input">
          <Select
            className="editor-select max-w-xl"
            classNamePrefix="slct"
            isDisabled={!hasDisplayNewLabel}
            name="periodNewLabel"
            options={periodLabelOptionList}
            placeholder={t("period.placeholder")}
            onChange={(
              selectedOption: SingleValue<PeriodLabelOption>,
            ): void => {
              onChange(
                "period_new_label",
                selectedOption ? selectedOption.value : "week",
              );
            }}
            value={
              periodLabelOptionList.find(
                (option) => option.value === periodNewLabel,
              ) ?? periodLabelOptionList[0]
            }
          />
        </div>
      </div>
      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayLastChanceLabel ?? false}
            name="display_last_chance_label"
            onChange={handleChangeLastChanceLabel}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="text-overlay-foreground m-[0.3125rem] rounded-[0.234375rem] bg-[#5d5d5d] px-[0.3125rem] text-center font-semibold">
            <span className="block text-[0.78125rem]">
              {t("lastChanceLabel")}
            </span>
          </div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row">
        {t("periodLabel.periodLastChanceLabel")}
        <div className="editor-form-input">
          <Select
            className="editor-select max-w-xl"
            classNamePrefix="slct"
            isDisabled={!hasDisplayLastChanceLabel}
            name="periodLastChanceLabel"
            options={periodLabelOptionList}
            placeholder={t("period.placeholder")}
            onChange={(
              selectedOption: SingleValue<PeriodLabelOption>,
            ): void => {
              onChange(
                "period_last_chance_label",
                selectedOption ? selectedOption.value : "week",
              );
            }}
            value={
              periodLabelOptionList.find(
                (option) => option.value === periodLastChanceLabel,
              ) ?? periodLabelOptionList[0]
            }
          />
        </div>
      </div>

      <div className="editor-form-row editor-form-row-inline">
        <div className="editor-form-input">
          <SwitchOnOff
            checked={hasDisplayComingSoonLabel ?? false}
            name="display_coming_soon_label"
            onChange={handleChangeComingSoonLabel}
          />
        </div>
        <div className="editor-form-label items-center">
          {t("display_label")}
          <div className="text-overlay-foreground m-[0.3125rem] rounded-[0.234375rem] bg-[#5d5d5d] px-[0.3125rem] text-center font-semibold">
            <span className="block text-[0.78125rem]">
              {t("comingSoonLabel")}
            </span>
          </div>
        </div>
        <InfoTooltip width="500px">
          {t("display_labels_tooltip")}
          <a
            className="link"
            href={t("label_display_help_url")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("moreInfos")}
          </a>
        </InfoTooltip>
      </div>
      <div className="editor-form-row">
        {t("periodLabel.periodComingSoonLabel")}
        <div className="editor-form-input">
          <Select
            className="editor-select max-w-xl"
            classNamePrefix="slct"
            isDisabled={!hasDisplayComingSoonLabel}
            name="periodComingSoonLabel"
            options={periodLabelOptionList}
            placeholder={t("period.placeholder")}
            onChange={(
              selectedOption: SingleValue<PeriodLabelOption>,
            ): void => {
              onChange(
                "period_coming_soon_label",
                selectedOption ? selectedOption.value : "week",
              );
            }}
            value={
              periodLabelOptionList.find(
                (option) => option.value === periodComingSoonLabel,
              ) ?? periodLabelOptionList[0]
            }
          />
        </div>
      </div>
    </div>
  );
}

export default LabelsConfiguration;
